/* eslint-disable react-hooks/exhaustive-deps */
// export default IncidentRequest;
import DeleteModal from "components/DeleteModal";
import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { FaBriefcase } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  deleteSchedule,
  fetchScheduleForCoordinator,
  fetchScheduleForPolice,
} from "services/prosecutorPoliceService";
import { Roles, Schedule } from "types";
import { DateView, errorHandler, hasRole } from "utils/helper";
import PageHeader from "../../components/PageHeader";
import ScheduleForm from "./modal/ScheduleForm";

// const tabs = ["All", "Approved", "Cancelled"];

const ScheduleList: React.FC = () => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const [action, setAction] = useState<"Add" | "Edit">("Add");
  const [isOpenForm, setIsOpenForm] = React.useState(false);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSchedule, setSelectedSchedule] = React.useState<Schedule>();
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  const fetchData = () => {
    setLoading(true);
    const api =
      loginData?.role === "Member" || loginData?.is_police
        ? fetchScheduleForPolice(loginData?.organization?.uu_id as any)
        : fetchScheduleForCoordinator(loginData?.admin_unit as any);
    api
      .then((data) => {
        setSchedules(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    if (loginData) fetchData();
  }, [loginData]);

  const handleOpenForm = () => {
    setIsOpenForm(true);
  };
  const handleCloseForm = (refetch = false) => {
    setIsOpenForm(false);

    if (refetch) fetchData();
  };

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <PageHeader title={t("schedule.list.page_title")} icon={FaBriefcase} />

      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                {t("schedule.list.table_title")}
              </h2>
              {hasRole(loginData?.roles, [
                Roles.prosecutionDirector,
                Roles.coordinator,
              ]) && (
                <button
                  onClick={() => {
                    setAction("Add");
                    handleOpenForm();
                  }}
                  className="inline-flex items-center px-4 py-2 bg-[#E90102] text-white font-medium rounded-lg hover:bg-[#c80102] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E90102]"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  {t("schedule.list.add_new")}
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="py-12">
              <Loading label={t("schedule.list.loading")} />
            </div>
          ) : schedules.length === 0 ? (
            <div className="py-16 text-center">
              <div className="mx-auto w-24 h-24 text-gray-300 mb-4">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t("schedule.list.no_records")}
              </h3>
              <p className="text-gray-500 max-w-md mx-auto mb-6">
                {t("schedule.list.no_records_description")}
              </p>
              {hasRole(loginData?.roles, [
                Roles.prosecutionDirector,
                Roles.coordinator,
              ]) && (
                <button
                  onClick={() => {
                    setAction("Add");
                    handleOpenForm();
                  }}
                  className="inline-flex items-center px-4 py-2 bg-[#E90102] text-white font-medium rounded-lg hover:bg-[#c80102] transition-colors duration-200"
                >
                  {t("schedule.list.create_first")}
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {t("police_station")}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {t("schedule.list.contact")}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {t("schedule.list.start_date_time")}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {t("schedule.list.end_date_time")}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {t("status")}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {t("action")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {schedules.map((schedule, index) => {
                    const startDate = new Date(
                      schedule.starting_date_time as any,
                    );
                    const endDate = new Date(schedule.end_date_time as any);
                    const now = new Date();
                    const isActive = now >= startDate && now <= endDate;
                    const isUpcoming = now < startDate;

                    return (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 transition-colors duration-150"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <svg
                                className="h-5 w-5 text-blue-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                />
                              </svg>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {schedule.police_station_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {schedule.org_phone_number}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {DateView(schedule.starting_date_time)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {startDate.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {DateView(schedule.end_date_time)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {endDate.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                              isActive
                                ? "bg-green-100 text-green-800"
                                : isUpcoming
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {isActive
                              ? t("schedule.list.active")
                              : isUpcoming
                                ? t("schedule.list.upcoming")
                                : t("completed")}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {hasRole(loginData?.roles, [
                              Roles.prosecutionDirector,
                              Roles.coordinator,
                            ]) && (
                              <>
                                <button
                                  onClick={() => {
                                    setAction("Edit");
                                    setSelectedSchedule(schedule);
                                    setIsOpenForm(true);
                                  }}
                                  className="text-indigo-600 hover:text-indigo-900 transition-colors"
                                >
                                  {t("edit")}
                                </button>
                                <button
                                  onClick={() => {
                                    setIsOpenDelete(true);
                                    setSelectedSchedule(schedule);
                                  }}
                                  className="text-red-600 hover:text-red-900 transition-colors"
                                >
                                  {t("schedule.list.delete")}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <ScheduleForm
          action={action}
          // schedule={undefined}
          schedule={action === "Edit" ? selectedSchedule : undefined}
          isOpen={isOpenForm}
          onClose={handleCloseForm}
        />

        {selectedSchedule && (
          <DeleteModal
            isOpen={isOpenDelete}
            onConfirm={() => {
              deleteSchedule(selectedSchedule)
                .then(() => {
                  showTooltip(
                    t(
                      "witness_protection.success_messages.successfully_deleted",
                    ),
                    "success",
                  );
                  fetchData();
                })
                .catch((error) => {
                  showTooltip(
                    error?.response?.data?.detail || errorHandler(error),
                  );
                });
            }}
            onCancel={() => {
              setIsOpenDelete(false);
            }}
            title={t("schedule.list.delete_title")}
            message={t("schedule.list.delete_confirmation", {
              policeStation: selectedSchedule?.police_station_name,
            })}
          />
        )}
      </div>
    </div>
  );
};

export default ScheduleList;
